import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Clock } from 'lucide-react';

interface Props {
  onBack: () => void;
  onSave?: () => void;
}

interface TimePickerProps {
  value: string; // e.g. "05:01 AM"
  onChange: (val: string) => void;
}

const HOURS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const PERIODS = ['AM', 'PM'];

const TimePickerInput: React.FC<TimePickerProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Parse time value
  const parseTime = (timeStr: string) => {
    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match) {
      return {
        hour: match[1].padStart(2, '0'),
        minute: match[2],
        period: match[3].toUpperCase(),
      };
    }
    return { hour: '05', minute: '01', period: 'AM' };
  };

  const { hour, minute, period } = parseTime(value);

  // Handle outside click to close popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelectHour = (newHour: string) => {
    onChange(`${newHour}:${minute} ${period}`);
  };

  const handleSelectMinute = (newMinute: string) => {
    onChange(`${hour}:${newMinute} ${period}`);
  };

  const handleSelectPeriod = (newPeriod: string) => {
    onChange(`${hour}:${minute} ${newPeriod}`);
  };

  return (
    <div className="relative inline-block" ref={containerRef}>
      {/* Input Display Button */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white border rounded-md cursor-pointer transition-all w-32 select-none ${
          isOpen
            ? 'border-blue-600 ring-2 ring-blue-100 shadow-xs'
            : 'border-slate-300 hover:border-slate-400'
        }`}
      >
        <span className="text-xs sm:text-sm font-mono font-medium text-slate-800 tracking-wide">
          {value}
        </span>
        <Clock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
      </div>

      {/* 3-Column Time Picker Dropdown matching screenshot */}
      {isOpen && (
        <div className="absolute bottom-full mb-1 left-0 z-50 bg-white border border-slate-300 rounded-md shadow-xl flex divide-x divide-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-100 text-xs sm:text-sm font-mono">
          {/* Column 1: Hours */}
          <div className="w-12 max-h-52 overflow-y-auto scrollbar-thin py-1 flex flex-col items-center">
            {HOURS.map((h) => {
              const isSelected = h === hour;
              return (
                <button
                  key={h}
                  type="button"
                  onClick={() => handleSelectHour(h)}
                  className={`w-full py-1.5 text-center transition-colors cursor-pointer ${
                    isSelected
                      ? 'bg-blue-600 text-white font-bold'
                      : 'text-slate-700 hover:bg-slate-100 font-normal'
                  }`}
                >
                  {h}
                </button>
              );
            })}
          </div>

          {/* Column 2: Minutes */}
          <div className="w-12 max-h-52 overflow-y-auto scrollbar-thin py-1 flex flex-col items-center">
            {MINUTES.map((m) => {
              const isSelected = m === minute;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => handleSelectMinute(m)}
                  className={`w-full py-1.5 text-center transition-colors cursor-pointer ${
                    isSelected
                      ? 'bg-blue-600 text-white font-bold'
                      : 'text-slate-700 hover:bg-slate-100 font-normal'
                  }`}
                >
                  {m}
                </button>
              );
            })}
          </div>

          {/* Column 3: AM / PM */}
          <div className="w-12 py-1 flex flex-col items-center">
            {PERIODS.map((p) => {
              const isSelected = p === period;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => handleSelectPeriod(p)}
                  className={`w-full py-1.5 text-center transition-colors cursor-pointer ${
                    isSelected
                      ? 'bg-blue-600 text-white font-bold'
                      : 'text-slate-700 hover:bg-slate-100 font-normal'
                  }`}
                >
                  {p}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const AutoReplySettingsView: React.FC<Props> = ({ onBack, onSave }) => {
  const [enabled, setEnabled] = useState(true);
  const [message, setMessage] = useState(
    `Thank you for your message.
This is an automated reply message.
Our operation hours are from 08:30 A.M. to 06:00 P.M. daily.
We will contact you very soon.
For urgent issues, please call us at 065-731-5882 or 065-569-6136.
Thank you.

สวัสดีค่ะ นี่คือข้อความอัตโนมัติ
เวลาทำการทุกวัน 08:30-18:00 น.
เราจะรีบติดต่อกลับท่านโดยเร็วที่สุด
หากมีเหตุฉุกเฉินระหว่างการพักอาศัย
กรุณาโทร 065-731-5882 หรือ 065-569-6136
ขอบคุณค่ะ`
  );
  const [startTime, setStartTime] = useState('05:01 AM');
  const [endTime, setEndTime] = useState('06:26 AM');
  const [days, setDays] = useState({
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false,
  });

  const handleDayToggle = (dayKey: keyof typeof days) => {
    setDays((prev) => ({ ...prev, [dayKey]: !prev[dayKey] }));
  };

  const handleSave = () => {
    if (onSave) onSave();
  };

  return (
    <div className="bg-white min-h-[550px] rounded-xl p-6 sm:p-8 font-prompt space-y-6">
      {/* Top Header */}
      <div className="flex items-center justify-between pb-2">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="w-8 h-8 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
            title="ย้อนกลับ"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
            ตั้งค่าการตอบกลับข้อความ
          </h1>
        </div>

        <button
          type="button"
          onClick={handleSave}
          className="px-5 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-sm font-semibold transition-colors shadow-xs cursor-pointer"
        >
          บันทึก
        </button>
      </div>

      <div className="space-y-6 pt-2">
        {/* Toggle Switch */}
        <div className="flex items-center justify-between py-2 border-b border-slate-100">
          <span className="text-sm sm:text-base font-medium text-slate-700">
            แสดงข้อความตอบกลับอัตโนมัติ
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={() => setEnabled(!enabled)}
            className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer ${
              enabled ? 'bg-blue-700 justify-end' : 'bg-slate-300 justify-start'
            }`}
          >
            <div className="bg-white w-4 h-4 rounded-full shadow-md" />
          </button>
        </div>

        {/* Textarea with Character Counter */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm sm:text-base font-medium text-slate-700">
              ข้อความตอบกลับ
            </label>
            <span className="text-xs sm:text-sm text-slate-500 font-mono">
              {message.length}/500
            </span>
          </div>

          <textarea
            rows={10}
            maxLength={500}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="กรอกข้อความตอบกลับอัตโนมัติที่นี่..."
            className="w-full p-4 text-xs sm:text-sm text-slate-700 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent leading-relaxed"
          />
        </div>

        {/* Working Hours with Interactive 3-Column Dropdown */}
        <div className="flex flex-wrap items-center gap-3 pt-2">
          <span className="text-xs sm:text-sm font-semibold text-slate-800">
            Working Hours <span className="text-rose-500">*</span> :
          </span>

          <div className="flex items-center gap-2">
            <TimePickerInput value={startTime} onChange={setStartTime} />
            <span className="text-slate-500 font-bold">-</span>
            <TimePickerInput value={endTime} onChange={setEndTime} />
          </div>
        </div>

        {/* Days of the Week Checkboxes */}
        <div className="flex flex-wrap items-center gap-4 sm:gap-6 pt-2">
          {[
            { key: 'monday', label: 'วันจันทร์' },
            { key: 'tuesday', label: 'วันอังคาร' },
            { key: 'wednesday', label: 'วันพุธ' },
            { key: 'thursday', label: 'วันพฤหัสบดี' },
            { key: 'friday', label: 'วันศุกร์' },
            { key: 'saturday', label: 'วันเสาร์' },
            { key: 'sunday', label: 'วันอาทิตย์' },
          ].map((day) => {
            const isChecked = days[day.key as keyof typeof days];
            return (
              <label
                key={day.key}
                className="flex items-center gap-2 cursor-pointer select-none text-xs sm:text-sm text-slate-700"
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => handleDayToggle(day.key as keyof typeof days)}
                  className="w-4 h-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500 cursor-pointer"
                />
                <span>{day.label}</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};
